"use client";

import { Button } from "@/components/atoms/Button";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  cardClick,
  leadCtaClick,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import Image from "next/image";
import { useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";

interface MetaItem {
  id: string;
  meta_title: string;
  meta_description: string;
  meta_priority: number;
  meta_status: boolean;
  meta_hover_color?: string;
  meta_title_color?: string;
  meta_background_color?: string;
  meta_description_color?: string;
  meta_icon?: string;
  meta_alt_icon?: string;
  meta_text_hover_color?: string;
}

interface RecruitersData {
  id: number;
  page_id: number;
  component_id: number;
  title: string;
  title_text_color?: string;
  sub_title?: string | null;
  sub_title_text_color?: string;
  desktop_bg_image: string;
  mobile_bg_image: string;
  alt_image?: string;
  background_color?: string | null;
  cta_text?: string | null;
  cta_text_color?: string | null;
  cta_background_color?: string | null;
  cta_button_text?: string | null;
  cta_button_text_color?: string | null;
  cta_button_color?: string | null;
  cta_button_icon?: string | null;
  cta_button_icon_alt?: string | null;
  metadata: MetaItem[];
  variant: string;
  status: number;
}

const Recruiters1 = ({ data }: { data: { data: RecruitersData } }) => {
  const isMobile = useIsMobile(800);
  const ref = useRef<HTMLElement>(null);
  const dispatch = useDispatch();

  // Handle both single and double nesting from PreviewRenderer
  const sectionData = (data as any)?.data || data;

  // Destructure with safe defaults so hooks can run unconditionally
  const {
    title = "",
    title_text_color = "#000000",
    sub_title,
    sub_title_text_color = "#000000",
    desktop_bg_image = "",
    mobile_bg_image = "",
    alt_image = "",
    background_color,
    cta_button_text,
    cta_button_text_color,
    cta_button_color,
    cta_button_icon,
    cta_button_icon_alt,
    metadata = [],
  } = sectionData || {};

  // Handle null values with nullish coalescing
  const bgColor = background_color ?? "#FFFFFF";
  const buttonText = cta_button_text ?? "";
  const buttonTextColor = cta_button_text_color ?? "#FFFFFF";
  const buttonColor = cta_button_color ?? "#BE2330";
  const buttonIcon = cta_button_icon ?? "";
  const buttonIconAlt = cta_button_icon_alt ?? "";

  // Sort metadata by priority and filter by status (hook runs unconditionally)
  const sortedMetadata = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item: MetaItem) => item.meta_status)
      .sort((a: MetaItem, b: MetaItem) => a.meta_priority - b.meta_priority);
  }, [metadata]);

  // Choose image based on device
  const image = isMobile
    ? mobile_bg_image || desktop_bg_image
    : desktop_bg_image || mobile_bg_image;

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // Track widget viewed (hook runs unconditionally)
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "recruiters_section");
  //   }
  // }, []);

  // Handle card click with tracking (hook runs unconditionally)
  const handleCardClick = useCallback(
    (item: MetaItem, index: number) => {
      cardClick({
        section: "recruiters_section",
        component: "recruiter_card",
        label: item.meta_title || `Recruiter ${index + 1}`,
        parent: title,
        redirectLink: "",
        pageKey: "",
      });
    },
    [title],
  );

  // Handle CTA button click with tracking (hook runs unconditionally)
  const handleOpenLeadForm = useCallback(() => {
    leadCtaClick({
      section: "recruiters_section",
      component: "cta_button",
      label: buttonText,
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [dispatch, buttonText]);

  // Early returns after all hooks to preserve hooks order
  if (!sectionData || !sectionData?.status) {
    return null;
  }
  if (!image || sortedMetadata.length === 0) {
    return null;
  }

  return (
    <section
      ref={ref}
      className={cn(
        "w-full flex flex-col items-center justify-center px-5 py-8 sm:py-11 ",
      )}
      style={{ backgroundColor: bgColor }}
    >
      <div
        className={cn(
          "flex flex-col items-center sm:flex-row justify-center w-full gap-6 sm:gap-11 md:gap-[62px] xl:gap-25",
          "sm:w-[1008px] md:w-[1158px] lg:w-[1358px] xl:w-[1678px]",
          "lg:gap-10",
        )}
      >
        <div
          className={cn(
            "flex flex-col sm:flex-row-reverse items-center justify-center",
            "gap-6",
            "sm:gap-11",
            "md:gap-17",
          )}
        >
          <div
            className={cn(
              "shrink-0",
              "xs:w-[158px] xs:h-[180px] shrink-0",
              "sm:w-[314px] sm:h-[362px]",
              "md:w-[360px] md:h-[416px]",
              "lg:w-[416px] lg:h-[480px]",
              "xl:w-[470px] xl:h-[536px]",
            )}
          >
            {image && (
              <Image
                src={image}
                alt={alt_image || "recruiters"}
                width={460}
                height={530}
                className="w-full h-full shrink-0 object-fill"
              />
            )}
          </div>

          <div className="flex flex-col items-center sm:items-start justify-center gap-6">
            <div className="flex flex-col gap-2">
              <h2
                className={cn(
                  "text-heading-6-semibold line-clamp-2",
                  "text-center sm:text-left",
                  "sm:text-heading-5-semibold",
                  "md:text-heading-4-semibold",
                  "lg:text-new-heading-34-semibold",
                  "xl:text-new-heading-42-semibold",
                  "type-h3",
                )}
                style={{ color: title_text_color }}
              >
                {title}
              </h2>
              {sub_title && (
                <p
                  className={cn(
                    "text-body-3-regular line-clamp-2",
                    "text-center sm:text-left",
                    "sm:text-body-2-regular",
                    "md:text-body-1-regular",
                    "lg:text-new-body-20-regular",
                    "xl:text-new-body-24-regular",
                  )}
                  style={{ color: sub_title_text_color }}
                >
                  {sub_title}
                </p>
              )}
            </div>

            {sortedMetadata &&
              sortedMetadata.length > 0 &&
              sortedMetadata.map((item, index) => (
                <div
                  className={cn(
                    "border-l-3",
                    "flex flex-col px-3 py-3 gap-2 rounded-4 w-[320px] transition-colors",
                    "w-full sm:px-8 sm:py-3",
                    "cursor-pointer",
                  )}
                  key={item.id}
                  onClick={() => handleCardClick(item, index)}
                  style={
                    {
                      backgroundColor: item.meta_background_color || "#FFF3F3",
                      borderLeftColor: item.meta_hover_color || "#0a3d5b",
                    } as React.CSSProperties
                  }
                  onMouseEnter={(e) => {
                    const hoverColor = item.meta_hover_color || "#0a3d5b";
                    const textHoverColor =
                      item.meta_text_hover_color || "#FFFFFF";
                    e.currentTarget.style.backgroundColor = hoverColor;

                    // Change all text colors on hover
                    const textElements = e.currentTarget.querySelectorAll("p");
                    textElements.forEach((el) => {
                      (el as HTMLElement).style.color = textHoverColor;
                    });
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      item.meta_background_color || "#FFF3F3";

                    // Restore original text colors
                    const titleElement =
                      e.currentTarget.querySelector("p:first-of-type");
                    const descElement =
                      e.currentTarget.querySelector("p:last-of-type");

                    if (titleElement) {
                      (titleElement as HTMLElement).style.color =
                        item.meta_title_color ||
                        item.meta_description_color ||
                        "#000000";
                    }

                    if (descElement) {
                      (descElement as HTMLElement).style.color =
                        item.meta_description_color || "#000000";
                    }
                  }}
                >
                  {item?.meta_icon && (
                    <div className="flex items-center gap-2">
                      <Image
                        src={item.meta_icon || ""}
                        alt={item.meta_alt_icon || "icon"}
                        width={24}
                        height={24}
                        className="w-6 h-6 object-contain"
                      />
                    </div>
                  )}
                  <p
                    className={cn(
                      "text-body-2-regular",
                      "sm:text-heading-8-semibold",
                      "md:text-heading-7-semibold",
                      "xl:text-new-body-24-semibold",
                      "line-clamp-1",
                      "transition-colors",
                      "type-h6",
                    )}
                    style={{
                      color:
                        item.meta_title_color ||
                        item.meta_description_color ||
                        "#000000",
                    }}
                  >
                    {item.meta_title}
                  </p>
                  <p
                    className={cn(
                      "text-body-2-regular",
                      "sm:text-body-3-regular",
                      "md:text-body-2-regular",
                      "lg:text-body-2-regular",
                      "xl:text-body-1-regular",
                      "line-clamp-3 sm:line-clamp-2",
                      "transition-colors",
                      "type-body-sm",
                    )}
                    style={{ color: item.meta_description_color || "#000000" }}
                  >
                    {item.meta_description}
                  </p>
                </div>
              ))}
            <div className="self-center sm:self-start">
              {buttonText && (
                <Button
                  className={cn(
                    "text-heading-9-semibold flex gap-[6px] ",
                    "sm:text-heading-9-semibold sm:gap-[6px] sm:px-[14px] sm:py-[6px]",
                    "md:text-heading-7-semibold md:gap-[6px] md:px-[16px] md:py-[6px]",
                    "lg:text-heading-6-semibold lg:gap-[10px] lg:py-2 lg:px-6",
                    "xl:text-heading-4-semibold xl:gap-[10px] xl:py-2 xl:px-6",
                  )}
                  style={{
                    backgroundColor: buttonColor,
                    color: buttonTextColor,
                  }}
                  onClick={handleOpenLeadForm}
                >
                  {buttonText}
                  {buttonIcon && (
                    <div
                      className={cn(
                        "relative size-[18px]",
                        "sm:size-[26px]",
                        "md:size-8",
                        "lg:size-9",
                        "xl:size-9",
                      )}
                    >
                      <Image
                        src={buttonIcon}
                        alt={buttonIconAlt || "CTA"}
                        fill
                      />
                    </div>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Recruiters1;
